// PrimaryButton.js
// -----------------------------------------------------------------------------
// Consistent call-to-action button for NHV. Wraps the Joy <Button> and
// transparently supports three navigation modes:
//   - internal route : pass `to="/join"`  -> renders as react-router <Link>
//   - external link   : pass `href="https://..."` -> renders as <a> (new tab)
//   - plain action    : pass `onClick`    -> renders a normal button
//
// Props:
//   - children : node     (required)        button label
//   - to       : string   (default undef)   internal route (react-router Link)
//   - href     : string   (default undef)   external URL (opens new tab)
//   - variant  : 'solid'|'soft'|'outlined'|'plain' (default 'solid')
//   - color    : 'primary'|'accent'|'neutral'      (default 'primary')
//   - size     : 'sm'|'md'|'lg'             (default 'lg')
//   - startDecorator / endDecorator : node  (default undef) icons
//   - onClick  : func     (default undef)   click handler
//   - inverted : boolean  (default false)   white outline style for secondary
//                actions on dark surfaces (hero photos, brand bands)
//   - fullWidth: boolean  (default false)   stretch to container width
//   - disabled : boolean  (default false)
//   - sx       : object   (default {})      extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/joy/Button';
import { Link as RouterLink } from 'react-router-dom';

const ALLOWED_VARIANTS = ['solid', 'soft', 'outlined', 'plain'];
const ALLOWED_COLORS = ['primary', 'accent', 'neutral'];
const ALLOWED_SIZES = ['sm', 'md', 'lg'];

// White-on-transparent outline for dark backgrounds, where the outlined
// primary/neutral colors would be too low-contrast.
const INVERTED_SX = {
  color: 'common.white',
  borderColor: 'rgba(255, 255, 255, 0.7)',
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.12)',
    borderColor: 'common.white',
  },
  '&:active': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
};

const PrimaryButton = ({
  children,
  to,
  href,
  variant = 'solid',
  color = 'primary',
  size = 'lg',
  startDecorator,
  endDecorator,
  onClick,
  inverted = false,
  fullWidth = false,
  disabled = false,
  sx = {},
  ...rest
}) => {
  // Zero-trust: constrain enum-like props to known sets. Inverted buttons are
  // always outlined neutral underneath, with the white overrides on top.
  const resolvedVariant = inverted
    ? 'outlined'
    : ALLOWED_VARIANTS.includes(variant) ? variant : 'solid';
  const resolvedColor = inverted
    ? 'neutral'
    : ALLOWED_COLORS.includes(color) ? color : 'primary';
  const resolvedSize = ALLOWED_SIZES.includes(size) ? size : 'lg';

  // Build the polymorphic props for the chosen navigation mode. Only one of
  // to / href is honored; `to` wins if both are (mistakenly) supplied.
  const navProps = {};
  if (typeof to === 'string' && to.length > 0) {
    navProps.component = RouterLink;
    navProps.to = to;
  } else if (typeof href === 'string' && href.length > 0) {
    navProps.component = 'a';
    navProps.href = href;
    navProps.target = '_blank';
    navProps.rel = 'noopener noreferrer';
  }

  return (
    <Button
      variant={resolvedVariant}
      color={resolvedColor}
      size={resolvedSize}
      startDecorator={startDecorator}
      endDecorator={endDecorator}
      onClick={onClick}
      disabled={disabled}
      sx={{ width: fullWidth ? '100%' : 'auto', ...(inverted ? INVERTED_SX : {}), ...sx }}
      {...navProps}
      {...rest}
    >
      {children}
    </Button>
  );
};

PrimaryButton.propTypes = {
  children: PropTypes.node.isRequired,
  to: PropTypes.string,
  href: PropTypes.string,
  variant: PropTypes.oneOf(ALLOWED_VARIANTS),
  color: PropTypes.oneOf(ALLOWED_COLORS),
  size: PropTypes.oneOf(ALLOWED_SIZES),
  startDecorator: PropTypes.node,
  endDecorator: PropTypes.node,
  onClick: PropTypes.func,
  inverted: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default PrimaryButton;
